import { Spot, UserLocation, EventSession } from '../types/spot';
import { calculateDistance, toRealisticRouteDistance } from './geolocation';

export type DayMoment = 'morning' | 'lunch' | 'afternoon' | 'sunset' | 'night';

export interface DayMomentInfo {
  id: DayMoment;
  emoji: string;
  labelDe: string;
  labelEn: string;
  labelKr: string;
  timeRange: string;
  vibeTipDe: string;
  vibeTipEn: string;
  vibeTipKr: string;
}

export const DAY_MOMENTS: Record<DayMoment, DayMomentInfo> = {
  morning: {
    id: 'morning',
    emoji: '🌅',
    labelDe: 'Morgen & Vormittag',
    labelEn: 'Morning & Fresh Air',
    labelKr: '오전 & 아침 산책',
    timeRange: '07:00 – 11:30',
    vibeTipDe: 'Beste Zeit für ruhige Paläste, Hanok-Gassen und Natur vor dem Touristenansturm.',
    vibeTipEn: 'Ideal for peaceful palaces, Hanok alleys, and temples before crowds arrive.',
    vibeTipKr: '인파가 몰리기 전 고요한 궁궐, 한옥 골목, 사찰을 둘러보기 가장 좋은 시간입니다.',
  },
  lunch: {
    id: 'lunch',
    emoji: '🥢',
    labelDe: 'Mittagszeit & Food',
    labelEn: 'Lunch & Food Stalls',
    labelKr: '점심 & 맛집 탐방',
    timeRange: '11:30 – 14:00',
    vibeTipDe: 'Perfekt für Food-Markets, traditionelle Garküchen, Cafés und Indoor-Malls.',
    vibeTipEn: 'Perfect for traditional food markets, street eats, cafes, and indoor hubs.',
    vibeTipKr: '전통 시장, 길거리 음식, 카페 및 대형 몰에서 맛있는 점심을 즐기기 좋습니다.',
  },
  afternoon: {
    id: 'afternoon',
    emoji: '☀️',
    labelDe: 'Nachmittag & Kultur',
    labelEn: 'Afternoon & Culture',
    labelKr: '오후 & 쇼핑/문화',
    timeRange: '14:00 – 17:30',
    vibeTipDe: 'Super für Fotospots, Museen, K-Fashion Shopping & Architektur-Spaziergänge.',
    vibeTipEn: 'Great for photo spots, museums, K-fashion streets, and urban walks.',
    vibeTipKr: '포토스팟, 박물관/전시, K-패션 쇼핑 및 트렌디한 도심 산책에 최적입니다.',
  },
  sunset: {
    id: 'sunset',
    emoji: '🌇',
    labelDe: 'Golden Hour & Sunset',
    labelEn: 'Golden Hour & Sunset',
    labelKr: '골든아워 & 일몰',
    timeRange: '17:30 – 19:30',
    vibeTipDe: 'Beste Sicht! Aussichtstürme, Brücken, Han-Flussufer und Sky-Bars jetzt besuchen.',
    vibeTipEn: 'Prime visual hour! Head to sky observatories, bridges, Han River, and rooftops.',
    vibeTipKr: '최고의 전망 시간대! 전망 타워, 한강변, 다리 및 루프탑에서 노을을 감상하세요.',
  },
  night: {
    id: 'night',
    emoji: '✨',
    labelDe: 'Nacht & Neonlicht',
    labelEn: 'Night & Skyline Lights',
    labelKr: '야경 & 나이트라이프',
    timeRange: '19:30 – 02:00',
    vibeTipDe: 'Beleuchtete Paläste, beleuchtete Brücken, Nachtmärkte und lebhafte Partyviertel.',
    vibeTipEn: 'Illuminated palaces, drone shows, night markets, and vibrant neon districts.',
    vibeTipKr: '야간 개장 궁궐, 드론 라이트쇼, 야시장 및 화려한 도심 야경을 만끽하세요.',
  },
};

/**
 * Returns current moment of day based on hours (or overridden hour for testing/simulating)
 */
export function getCurrentDayMoment(customHour?: number): DayMoment {
  const hour = customHour !== undefined ? customHour : new Date().getHours() + new Date().getMinutes() / 60;

  if (hour >= 7 && hour < 11.5) return 'morning';
  if (hour >= 11.5 && hour < 14) return 'lunch';
  if (hour >= 14 && hour < 17.5) return 'afternoon';
  if (hour >= 17.5 && hour < 19.5) return 'sunset';
  return 'night';
}

/**
 * Evaluates whether a spot is in its prime moment right now
 */
export function isSpotPerfectNow(spot: Spot, moment: DayMoment): boolean {
  const textToScan = `${spot.bestTime} ${spot.bestTimeDe} ${spot.category} ${spot.tags.join(' ')} ${spot.name}`.toLowerCase();

  switch (moment) {
    case 'morning':
      return (
        spot.category === 'palace_heritage' ||
        textToScan.includes('morning') ||
        textToScan.includes('vormittag') ||
        textToScan.includes('früh') ||
        textToScan.includes('09:00') ||
        textToScan.includes('10:00')
      );

    case 'lunch':
      return (
        spot.category === 'vegetarian_food' ||
        textToScan.includes('lunch') ||
        textToScan.includes('mittag') ||
        textToScan.includes('street food') ||
        textToScan.includes('market') ||
        textToScan.includes('markt') ||
        textToScan.includes('food') ||
        textToScan.includes('chinatown')
      );

    case 'afternoon':
      return (
        spot.category === 'shopping' ||
        spot.category === 'photo_spot' ||
        spot.category === 'modern_architecture' ||
        spot.category === 'cultural_hidden' ||
        spot.category === 'activity' ||
        textToScan.includes('afternoon') ||
        textToScan.includes('nachmittag') ||
        textToScan.includes('shopping') ||
        textToScan.includes('mall')
      );

    case 'sunset':
      return (
        spot.category === 'nature_viewpoint' ||
        textToScan.includes('sunset') ||
        textToScan.includes('sonnenuntergang') ||
        textToScan.includes('golden hour') ||
        textToScan.includes('observatory') ||
        textToScan.includes('aussicht') ||
        textToScan.includes('tower') ||
        textToScan.includes('bridge') ||
        textToScan.includes('turm')
      );

    case 'night':
      return (
        spot.category === 'night_view' ||
        textToScan.includes('night') ||
        textToScan.includes('nacht') ||
        textToScan.includes('abend') ||
        textToScan.includes('evening') ||
        textToScan.includes('illuminated') ||
        textToScan.includes('beleucht') ||
        textToScan.includes('skyline') ||
        textToScan.includes('drone') ||
        textToScan.includes('ferris wheel')
      );

    default:
      return false;
  }
}

/**
 * Detects if a spot is protected from rain or weather (indoor, underground, museum, mall)
 */
export function isIndoorSpot(spot: Spot): boolean {
  if (spot.isIndoor) return true;
  const text = `${spot.name} ${spot.nameDe} ${spot.description} ${spot.descriptionDe} ${spot.tags.join(' ')}`.toLowerCase();
  return (
    text.includes('indoor') ||
    text.includes('untergrund') ||
    text.includes('underground') ||
    text.includes('mall') ||
    text.includes('museum') ||
    text.includes('library') ||
    text.includes('bibliothek') ||
    text.includes('observatory') ||
    text.includes('aquarium') ||
    text.includes('coex') ||
    text.includes('bupyeong') ||
    text.includes('tower') ||
    text.includes('turm') ||
    text.includes('g-tower') ||
    text.includes('lotte world') ||
    text.includes('minigolf') ||
    text.includes('screen golf') ||
    text.includes('escape room') ||
    text.includes('bouldering') ||
    text.includes('archery') ||
    text.includes('roller') ||
    text.includes('ice rink')
  );
}

/**
 * Finds cluster spots within walking distance (< 500m / 0.5km) to combine with zero extra detour
 */
export function findNearbyClusterSpots(
  currentSpot: Spot,
  allSpots: Spot[],
  maxDistKm: number = 0.5
): { spot: Spot; distanceMeters: number }[] {
  return allSpots
    .filter((s) => s.id !== currentSpot.id)
    .map((s) => {
      const km = calculateDistance(currentSpot.lat, currentSpot.lng, s.lat, s.lng);
      return {
        spot: s,
        distanceMeters: Math.round(km * 1000),
      };
    })
    .filter((item) => item.distanceMeters <= maxDistKm * 1000)
    .sort((a, b) => a.distanceMeters - b.distanceMeters);
}

/**
 * Finds nearby verified Halal, Universal, and Vegetarian/Vegan dining spots relative to any given spot.
 * Filters and sorts by shortest distance, prioritizing spots in the same region or within scenic reach.
 */
export function findNearbyHalalAndVeggieSpots(
  currentSpot: Spot,
  allSpots: Spot[],
  maxDistKm: number = 12
): { spot: Spot; distanceKm: number; distanceMeters: number }[] {
  return allSpots
    .filter((s) => s.id !== currentSpot.id)
    .filter(
      (s) =>
        s.category === 'halal_food' ||
        s.category === 'vegetarian_food' ||
        s.isHalal ||
        s.isVegetarianOnly ||
        s.dietType === 'vegan' ||
        s.dietType === 'vegetarian' ||
        s.dietType === 'halal' ||
        s.dietType === 'universal' ||
        s.tags.some(
          (t) =>
            t.toLowerCase().includes('halal') ||
            t.toLowerCase().includes('vegan') ||
            t.toLowerCase().includes('veggie') ||
            t.toLowerCase().includes('vegetarian')
        )
    )
    .map((s) => {
      const km = calculateDistance(currentSpot.lat, currentSpot.lng, s.lat, s.lng);
      return {
        spot: s,
        distanceKm: Math.round(km * 10) / 10,
        distanceMeters: Math.round(km * 1000),
      };
    })
    .filter((item) => {
      if (currentSpot.region === item.spot.region) {
        return item.distanceKm <= maxDistKm;
      }
      return item.distanceKm <= 5;
    })
    .sort((a, b) => a.distanceKm - b.distanceKm);
}

/**
 * Nearest-Neighbor TSP optimization for minimal walking distance in a multi-spot route
 */
export function optimizeRouteWalkingOrder(
  spots: Spot[],
  startPoint?: { lat: number; lng: number }
): {
  orderedSpots: Spot[];
  originalDistanceKm: number;
  optimizedDistanceKm: number;
  savedDistanceKm: number;
  savedPercent: number;
} {
  if (spots.length <= 2) {
    const origDist = calculateRouteTotalDistance(spots, startPoint);
    return {
      orderedSpots: spots,
      originalDistanceKm: origDist,
      optimizedDistanceKm: origDist,
      savedDistanceKm: 0,
      savedPercent: 0,
    };
  }

  const originalDistanceKm = calculateRouteTotalDistance(spots, startPoint);

  const remaining = [...spots];
  const ordered: Spot[] = [];

  // Start with closest to starting point or first spot
  let currentCoord = startPoint || { lat: spots[0].lat, lng: spots[0].lng };

  while (remaining.length > 0) {
    let bestIndex = 0;
    let bestDist = Infinity;

    for (let i = 0; i < remaining.length; i++) {
      const d = calculateDistance(currentCoord.lat, currentCoord.lng, remaining[i].lat, remaining[i].lng);
      if (d < bestDist) {
        bestDist = d;
        bestIndex = i;
      }
    }

    const nextSpot = remaining.splice(bestIndex, 1)[0];
    ordered.push(nextSpot);
    currentCoord = { lat: nextSpot.lat, lng: nextSpot.lng };
  }

  const optimizedDistanceKm = calculateRouteTotalDistance(ordered, startPoint);
  const savedDistanceKm = Math.max(0, originalDistanceKm - optimizedDistanceKm);
  const savedPercent = originalDistanceKm > 0 ? Math.round((savedDistanceKm / originalDistanceKm) * 100) : 0;

  return {
    orderedSpots: ordered,
    originalDistanceKm,
    optimizedDistanceKm,
    savedDistanceKm,
    savedPercent,
  };
}

function calculateRouteTotalDistance(spots: Spot[], startPoint?: { lat: number; lng: number }): number {
  if (spots.length === 0) return 0;
  let total = 0;

  if (startPoint) {
    total += calculateDistance(startPoint.lat, startPoint.lng, spots[0].lat, spots[0].lng);
  }

  for (let i = 0; i < spots.length - 1; i++) {
    total += calculateDistance(spots[i].lat, spots[i].lng, spots[i + 1].lat, spots[i + 1].lng);
  }

  return total;
}

/**
 * Calculates estimated taxi fare in KRW based on km distance (standard Seoul/Busan taxi base rates)
 */
export function estimateTaxiFareKrw(distanceKm?: number, language: string = 'en'): { fareKrw: number; fareDisplay: string } {
  const isDe = language === 'de';
  const isKr = language === 'kr';

  if (!distanceKm || isNaN(distanceKm) || distanceKm <= 0) {
    const baseLabel = isKr ? '기본요금' : isDe ? 'Grundpreis' : 'Base fare';
    return { fareKrw: 4800, fareDisplay: `~4,800 KRW (${baseLabel})` };
  }

  const roadKm = toRealisticRouteDistance(distanceKm);

  // Base fare in Seoul: 4,800 KRW for first 1.6 km
  // Approx 1,100 KRW per additional road km in urban traffic
  let fare = 4800;
  if (roadKm > 1.6) {
    fare += Math.round((roadKm - 1.6) * 1100);
  }

  // Round up to nearest 100 KRW
  fare = Math.ceil(fare / 100) * 100;
  const formatted = fare.toLocaleString('en-US');
  return {
    fareKrw: fare,
    fareDisplay: `~${formatted} KRW`,
  };
}

const MONTHS_DE = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS_SHORT_DE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const DAYS_SHORT_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_SHORT_KR = ['일', '월', '화', '수', '목', '금', '토'];
const DAYS_FULL_DE = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
const DAYS_FULL_EN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DAYS_FULL_KR = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];

/**
 * Returns upcoming event sessions filtered to today or later (in KST / local date).
 * For weekly recurring social events, automatically projects future weekly sessions if needed
 * so cards and modals never display outdated past dates.
 */
export function getUpcomingEventSessions(spot: Spot, referenceDate?: Date): EventSession[] {
  const sessions = spot.upcomingSessions;
  if (!sessions || sessions.length === 0) return [];

  const now = referenceDate || new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const todayIso = `${year}-${month}-${day}`;

  const upcoming = sessions.filter((s) => s.date >= todayIso);
  if (upcoming.length >= 4) {
    return upcoming;
  }

  // Project weekly recurring sessions forward from the last known sessions
  const result = [...upcoming];
  const seenDates = new Set(result.map((s) => s.date));
  const templates = sessions.slice(-Math.min(3, sessions.length));

  for (let weekOffset = 1; weekOffset <= 8 && result.length < 6; weekOffset++) {
    for (const tpl of templates) {
      const baseParts = tpl.date.split('-').map(Number);
      if (baseParts.length !== 3 || baseParts.some(isNaN)) continue;
      const dt = new Date(baseParts[0], baseParts[1] - 1, baseParts[2] + weekOffset * 7);
      const y = dt.getFullYear();
      const m = dt.getMonth();
      const d = dt.getDate();
      const dow = dt.getDay();
      const iso = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

      if (iso >= todayIso && !seenDates.has(iso)) {
        seenDates.add(iso);
        result.push({
          ...tpl,
          date: iso,
          dayNameDe: DAYS_FULL_DE[dow],
          dayNameEn: DAYS_FULL_EN[dow],
          dayNameKr: DAYS_FULL_KR[dow],
          dateLabelDe: `${DAYS_SHORT_DE[dow]}, ${String(d).padStart(2, '0')}. ${MONTHS_DE[m]} ${y}`,
          dateLabelEn: `${DAYS_SHORT_EN[dow]}, ${MONTHS_EN[m]} ${d}, ${y}`,
          dateLabelKr: `${m + 1}월 ${d}일 (${DAYS_SHORT_KR[dow]})`,
        });
      }
    }
  }

  return result.sort((a, b) => a.date.localeCompare(b.date));
}
